import { Box, Paper, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { store } from '../../../config/firebase';
import { GameStatus } from '../../../constants/gameConstants';
import { liveGameDocs } from '../../../helpers/games';
import PoweredBy from '../../ui/poweredBy';

// The overlay is designed as a fixed portrait stage - the 1080 x 1920 "HD setup".
// Everything inside it is positioned in plain stage pixels, so the graphic looks
// identical on any screen; the stage itself is scaled to fit the browser window.
const STAGE_WIDTH = 1080;
const STAGE_HEIGHT = 1920;

// Size of the team colour square, in pixels - tweak here to resize it.
const TEAM_COLOR_SQUARE_SIZE = 40;

// Width of the Fairtree logo, in stage pixels - tweak here to resize it. It is the
// tallest thing in row 1, so it sets the panel's height as well as its width.
const LOGO_WIDTH = 200;

// Corner radius of the panel, in pixels - tweak here to round it more or less.
// Applied as a pixel value: MUI multiplies a bare number by the theme's
// shape.borderRadius, so `8` there would come out far rounder than 8px.
const PANEL_BORDER_RADIUS = 8;

// Font size of the team names, in stage pixels - tweak here to resize them.
// Smaller than SCORE_FONT_SIZE, so the names stay subordinate to the figures.
const TEAM_NAME_FONT_SIZE = 24;

// Font size of the score figures, in stage pixels - tweak here to resize them.
const SCORE_FONT_SIZE = 32;

// Font size of the "Set 2 · End 5" label, in stage pixels - tweak here to resize it.
const POSITION_LABEL_FONT_SIZE = 28;

// Font size and weight of the section / round / green caption, in stage pixels -
// tweak here to resize it. The weight stays lighter than the team names, the
// figures and the sets/ends label, so it still reads as a caption.
const GAME_CAPTION_FONT_SIZE = 22;
const GAME_CAPTION_FONT_WEIGHT = 400;

// The panel hugs its content, so the padding and the gaps below are what set the
// size of the box around it. Values are theme spacing units - tweak here to resize
// it. The vertical ones are deliberately tight: this is a strip across the top of
// the stage, and row 1 is as tall as the logo, so slack here is pure height.
const PANEL_MARGIN = 3;
const PANEL_HORIZONTAL_PADDING = 3;
const PANEL_VERTICAL_PADDING = 1.5;
const POSITION_LABEL_MARGIN_BOTTOM = 1;
const POSITION_LINES_GAP = 0.5;
const TEAM_GRID_COLUMN_GAP = 2;
const TEAM_GRID_ROW_GAP = 1;

// Width of each team's score column, in stage pixels - fixed rather than fluid so
// the two teams' numbers still line up vertically.
const SCORE_COLUMN_WIDTH = 110;

// Team colours are stored as either a hex value or a theme palette key (e.g. 'green'),
// so resolve both the way the rest of the app does.
const HEX_COLOR_PATTERN = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/;

const getTeamColor = (color) => {
  if (!color) {
    // No colour to show - the square keeps its border so it stays visible.
    return 'transparent';
  }

  return HEX_COLOR_PATTERN.test(color) ? color : `${color}.main`;
};

// Ends points for a single set.
const getSetScore = (set, teamId) =>
  (set.scores || []).reduce((score, end) => score + (end.teamScores?.[teamId] || 0), 0);

// Ends points for the set currently being played (see teamEndScore.jsx).
const getEndScoreForCurrentSet = (game, teamId) => {
  const currentSet = game.sets?.find((set) => set.id === game.currentSet);

  // No current set yet, so there are no ends points to show.
  return currentSet ? getSetScore(currentSet, teamId) : 0;
};

// Ends points across every set - the fallback when the game has no teamTotals.
const getEndScoreOverSets = (game, teamId) =>
  (game.sets || []).reduce((score, set) => score + getSetScore(set, teamId), 0);

// Game points for the team, never undefined.
const getGamePoints = (game, teamId) =>
  game.teamTotals?.[teamId] ?? getEndScoreOverSets(game, teamId);

// Same name convention as teamEndScore.jsx: the team's own name, unless it is a
// placeholder, in which case join the players and abbreviate first names for teams
// of more than two.
const getTeamName = (team) => {
  const useTeamName = team.name !== 'Team 1' && team.name !== 'Team 2' && team.name;

  if (useTeamName) {
    return team.name;
  }

  const players = team.players || [];

  if (players.length === 0) {
    return team.id || 'Team';
  }

  return players
    .map((player) => {
      const [firstName, ...surname] = player.trim().split(' ');

      if (players.length > 2 && surname.length > 0) {
        return `${firstName.charAt(0)}. ${surname.join(' ')}`;
      }

      return player;
    })
    .join(', ');
};

// A figure that is safe to print: coerced to a number, with anything that is not
// a finite, positive number (undefined, null, NaN, zero, negatives) treated as
// unknown instead of being shown.
const toPositiveNumber = (value) => {
  const number = Number(value);

  return Number.isFinite(number) && number > 0 ? number : 0;
};

// "Set 2 · End 5" - where the game is right now, rather than the format it is
// played to. The set part follows the /embed/live convention of dropping the set
// figure for a single-set game, so it only appears when the game is played over
// more than one set (the format's set count, falling back to the sets on the
// game). Note `game.currentSet` is the current set's id, not an index, so it has
// to be looked up among the game's sets to find the one-based position; a current
// set that is unknown or matches no set drops the set part rather than printing a
// wrong number. The end part prefers the game's `currentEnd`, and otherwise
// counts the ends in the current set that have scores, the way gameLiveCard does.
// Anything that cannot be resolved is dropped, and this is an empty string when
// neither part is known.
const getLivePositionLabel = (game) => {
  const sets = Array.isArray(game.sets) ? game.sets : [];
  const currentSetIndex = sets.findIndex((set) => set.id === game.currentSet);
  const currentSet = currentSetIndex >= 0 ? sets[currentSetIndex] : null;

  // More than one set, from the format when it has it and from the sets
  // themselves when it does not - a single-set game has no set part at all.
  const setCount = toPositiveNumber(game.gameFormat?.sets) || toPositiveNumber(sets.length);

  // Ends with any team score on them, counted the way endsPlayed is in
  // gameLiveCard.jsx.
  const endsPlayed = Array.isArray(currentSet?.scores)
    ? currentSet.scores.filter((score) => Object.keys(score?.teamScores || {}).length > 0).length
    : 0;

  const currentEnd = toPositiveNumber(game.currentEnd) || toPositiveNumber(endsPlayed);
  const parts = [];

  if (setCount > 1 && currentSetIndex >= 0) {
    parts.push(`Set ${currentSetIndex + 1}`);
  }

  if (currentEnd > 0) {
    parts.push(`End ${currentEnd}`);
  }

  return parts.join(' · ');
};

// Ends points for the team, as the panel shows them. A playoff (tiebreaker) game
// scores in `game.playoff`, not in the set ends - TeamEndScore takes the same branch -
// so a playoff takes its tiebreaker points and everything else the running total for
// the set being played.
const getEndsPoints = (game, teamId) =>
  game.status === GameStatus.PLAYOFF
    ? toPositiveNumber(game.playoff?.teamScores?.[teamId])
    : getEndScoreForCurrentSet(game, teamId) || 0;

// Text that is safe to print in the caption: anything missing or unusable
// (undefined, null, NaN, an empty string, or a value that is not text at all)
// is dropped rather than printed, so the caption never shows a placeholder like
// "undefined" or leaves a dangling separator behind.
const toCaptionText = (value) => {
  if (typeof value !== 'string' && typeof value !== 'number') {
    return '';
  }

  const text = String(value).trim();

  return text === 'NaN' ? '' : text;
};

// "Section 2 - Round 6 A1" - which game this is, taken straight from the game's
// own `name`, which already holds the section, the round and the green/rink in
// exactly that form (the same string the /embed/live card shows). When there is
// no name the caption is composed from the game's own fields instead: the round
// plus the green and rink, e.g. "Round 6 A1", matching the way generateGameName
// builds the name on the server. Note the section is left out of that fallback -
// on a game document `game.section` is an id, so printing it would put an opaque
// id on a TV screen. Anything that cannot be resolved is dropped, and this is an
// empty string when nothing at all is known, which the panel renders as no line
// rather than a blank one.
const getGameCaption = (game) => {
  const name = toCaptionText(game.name);

  if (name) {
    return name;
  }

  // The round is a figure, so it gets the same "positive number or nothing"
  // treatment as the rest of the panel - a missing or zero round drops that part.
  const round = toPositiveNumber(game.round);

  // Green and rink read as one part ("A1") with nothing between them, the way
  // the game's name is built, so the two share a slot in the caption.
  const greenAndRink = `${toCaptionText(game.green)}${toPositiveNumber(game.rink) || ''}`;
  const parts = [];

  if (round > 0) {
    parts.push(`Round ${round}`);
  }

  if (greenAndRink) {
    parts.push(greenAndRink);
  }

  return parts.join(' ');
};

// The scale that fits the fixed stage into the window - the smaller of the two
// ratios, so the stage is always fully visible. 1 when there is no window (SSR).
const getStageScale = () =>
  typeof window === 'undefined'
    ? 1
    : Math.min(window.innerWidth / STAGE_WIDTH, window.innerHeight / STAGE_HEIGHT);

// One team's row across the panel: colour, name, game points, ends points. The
// game points cell is dropped when the panel hides that column, so a row always
// has exactly as many cells as the grid has tracks.
const TeamRow = ({ team, game, showGamePoints }) => (
  <>
    {/* Team colour */}
    <Box
      sx={{
        width: TEAM_COLOR_SQUARE_SIZE,
        height: TEAM_COLOR_SQUARE_SIZE,
        backgroundColor: getTeamColor(team.color),
        border: '1px solid',
        borderColor: 'divider'
      }}
    />

    {/* Team name */}
    <Typography fontSize={TEAM_NAME_FONT_SIZE} fontWeight={700} noWrap>
      {getTeamName(team)}
    </Typography>

    {/* Total game points - dropped with its column when neither team has any */}
    {showGamePoints && (
      <Typography fontSize={SCORE_FONT_SIZE} fontWeight={800} textAlign="center">
        {getGamePoints(game, team.id) || 0}
      </Typography>
    )}

    {/* Total ends points */}
    <Typography
      fontSize={SCORE_FONT_SIZE}
      fontWeight={700}
      color="text.secondary"
      textAlign="center">
      {getEndsPoints(game, team.id)}
    </Typography>
  </>
);

const HorizontalLiveScoreEvent = () => {
  const { eventId } = useParams();
  const [game, setGame] = useState(null);
  const [scale, setScale] = useState(getStageScale);

  useEffect(() => {
    const gamesRef = collection(store, 'games');
    const q = query(
      gamesRef,
      where('event', '==', eventId.toString()),
      where('live', '==', true),
      where('status', 'in', [GameStatus.IN_PROGRESS, GameStatus.PLAYOFF])
    );

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      // Filtered before the index, not after: taking docs[0] of the raw
      // snapshot would let a deleted game be the one shown as live.
      const liveDocs = liveGameDocs(querySnapshot);
      if (liveDocs.length > 0) {
        const firstGame = {
          id: liveDocs[0].id,
          ...liveDocs[0].data()
        };
        setGame(firstGame);
      } else {
        // If no games match the query, reset the state
        setGame(null);
      }
    });

    return () => unsubscribe(); // Cleanup listener on component unmount
  }, [eventId]);

  // Keep the stage fitted to the window: scale on mount (the window can change
  // between the first render and this effect) and again on every resize.
  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined; // Nothing to scale against outside the browser
    }

    const handleResize = () => setScale(getStageScale());

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (!game) {
    return null; // Render nothing while there is no live game
  }

  const teams = [game.teams?.teamA, game.teams?.teamB].filter(Boolean);
  const positionLabel = getLivePositionLabel(game);
  const caption = getGameCaption(game);

  // One decision for the whole panel, not per team: the game points column is
  // dropped only when neither team has any, so the two rows can never fall out of
  // line with each other.
  const showGamePoints = teams.some((team) => toPositiveNumber(getGamePoints(game, team.id)) > 0);

  return (
    // Clipping root, so the stage's own unscaled 1080 x 1920 layout box never
    // spills out as scrollbars - only the scaled graphic shows.
    <Box sx={{ position: 'fixed', inset: 0, overflow: 'hidden' }}>
      <Box
        sx={{
          width: `${STAGE_WIDTH}px`,
          height: `${STAGE_HEIGHT}px`,
          overflow: 'hidden',
          transform: `scale(${scale})`,
          transformOrigin: 'top left'
        }}>
        {/* The panel hugs its content in the top-left corner of the stage */}
        <Paper
          elevation={3}
          sx={{
            m: PANEL_MARGIN,
            // Hug the content rather than filling the 1080px stage: a Paper is a
            // block, so without this the panel spans the full width of the screen.
            width: 'fit-content',
            maxWidth: '100%',
            borderRadius: `${PANEL_BORDER_RADIUS}px`,
            px: PANEL_HORIZONTAL_PADDING,
            py: PANEL_VERTICAL_PADDING
          }}>
          {/* Row 1: where the game is up to, with which game it is stacked under
              it, boxed together on the left - the logo on the right */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: POSITION_LABEL_MARGIN_BOTTOM
            }}>
            {/* The game caption above the ends label, stacked as one left-aligned
                block so the caption reads as part of it rather than a row of its
                own. Only rendered when at least one of the two resolves, so the
                block never renders as an empty element. */}
            {positionLabel || caption ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  textAlign: 'left',
                  gap: POSITION_LINES_GAP
                }}>
                {caption ? (
                  <Typography
                    fontSize={GAME_CAPTION_FONT_SIZE}
                    fontWeight={GAME_CAPTION_FONT_WEIGHT}
                    color="text.secondary">
                    {caption}
                  </Typography>
                ) : null}

                {positionLabel ? (
                  <Typography fontSize={POSITION_LABEL_FONT_SIZE} fontWeight={800}>
                    {positionLabel}
                  </Typography>
                ) : null}
              </Box>
            ) : null}

            <PoweredBy width={`${LOGO_WIDTH}px`} />
          </Box>

          {/* Rows 3 and 4: one row per team. The score columns are fixed rather
              than fluid, so the two teams' numbers line up vertically with each
              other, and the game points track is dropped for both teams at once
              when neither has any - no fixed track is left behind. */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: showGamePoints
                ? `auto minmax(0, 1fr) ${SCORE_COLUMN_WIDTH}px ${SCORE_COLUMN_WIDTH}px`
                : `auto minmax(0, 1fr) ${SCORE_COLUMN_WIDTH}px`,
              columnGap: TEAM_GRID_COLUMN_GAP,
              rowGap: TEAM_GRID_ROW_GAP,
              alignItems: 'center'
            }}>
            {teams.map((team, index) => (
              <TeamRow
                key={team.id || index}
                team={team}
                game={game}
                showGamePoints={showGamePoints}
              />
            ))}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default HorizontalLiveScoreEvent;
